import { useMemo, useState } from 'react';
import './SimpleCharts.css';

function buildAreaGeometry({ data, xKey, yKey, width, height, padding }) {
  if (!data.length) {
    return null;
  }

  const values = data.map((row) => Number(row[yKey]) || 0);
  const maxY = Math.max(...values, 1);
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  const baseY = padding.top + chartHeight;

  const points = data.map((row, index) => {
    const x = padding.left + (data.length <= 1 ? chartWidth / 2 : (index / (data.length - 1)) * chartWidth);
    const y = padding.top + chartHeight - ((Number(row[yKey]) || 0) / maxY) * chartHeight;
    return { x, y, row, index };
  });

  const linePath = points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${baseY} L ${points[0].x} ${baseY} Z`;

  return { points, linePath, areaPath, maxY, chartHeight, baseY, padding };
}

function SimpleAreaChart({
  data = [],
  xKey = 'date',
  yKey = 'count',
  stroke = '#6e9abe',
  height = 220,
  formatXLabel = (value) => String(value ?? ''),
  formatTooltipLabel = (value) => String(value ?? ''),
  renderTooltip,
}) {
  const [activeIndex, setActiveIndex] = useState(null);
  const width = 640;
  const padding = { top: 16, right: 20, bottom: 52, left: 20 };

  const geometry = useMemo(
    () => buildAreaGeometry({ data, xKey, yKey, width, height, padding }),
    [data, xKey, yKey, height],
  );

  if (!geometry) {
    return <div className="simple-chart simple-chart-empty">Нет данных для графика</div>;
  }

  const { points, linePath, areaPath, chartHeight, baseY, padding: pad } = geometry;
  const activePoint = activeIndex == null ? null : points[activeIndex];

  return (
    <div className="simple-chart">
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} role="img" aria-label="График">
        {[0.25, 0.5, 0.75, 1].map((ratio) => {
          const y = pad.top + chartHeight * (1 - ratio);
          return (
            <line
              key={ratio}
              x1={pad.left}
              x2={width - pad.right}
              y1={y}
              y2={y}
              className="simple-chart-grid-line"
            />
          );
        })}
        <path d={areaPath} fill={stroke} fillOpacity="0.15" />
        <path d={linePath} fill="none" stroke={stroke} strokeWidth="3" />
        {points.map((point) => (
          <circle
            key={`${point.index}-${point.x}`}
            cx={point.x}
            cy={point.y}
            r={activeIndex === point.index ? 6 : 4}
            fill={stroke}
            className="simple-chart-dot"
            onMouseEnter={() => setActiveIndex(point.index)}
            onMouseLeave={() => setActiveIndex(null)}
            onFocus={() => setActiveIndex(point.index)}
            onBlur={() => setActiveIndex(null)}
            tabIndex={0}
          />
        ))}
        {points.map((point) => (
          <text
            key={`label-${point.index}`}
            x={point.x}
            y={baseY + 18}
            textAnchor="middle"
            className="simple-chart-axis-label"
          >
            {formatXLabel(point.row[xKey])}
          </text>
        ))}
      </svg>
      {activePoint && renderTooltip ? (
        <div className="simple-chart-tooltip">{renderTooltip(activePoint.row)}</div>
      ) : null}
      {activePoint && !renderTooltip ? (
        <div className="simple-chart-tooltip">
          <strong>{formatTooltipLabel(activePoint.row[xKey])}</strong>
          <span>{activePoint.row[yKey]}</span>
        </div>
      ) : null}
    </div>
  );
}

export default SimpleAreaChart;
